import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import styles from './styles.module.css';

function isInternalLink(href: string) {
  if (
    !href ||
    href.startsWith('#') ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:')
  ) {
    return false;
  }

  if (href.startsWith('http://') || href.startsWith('https://')) {
    try {
      return new URL(href).origin === window.location.origin;
    } catch {
      return false;
    }
  }

  return href.startsWith('/');
}

export const NavigationProgress = () => {
  const router = useRouter();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const pendingLinkRef = useRef<HTMLElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const clearPendingLink = () => {
      pendingLinkRef.current?.classList.remove('nav-link-pending');
      pendingLinkRef.current = null;
      document.body.classList.remove('is-navigating');
    };

    const completeNavigation = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setProgress(100);
      window.setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, 200);
      clearPendingLink();
    };

    const startNavigation = (
      _url: string,
      shallowOrCtx: boolean | { shallow?: boolean }
    ) => {
      const shallow =
        typeof shallowOrCtx === 'boolean'
          ? shallowOrCtx
          : Boolean(shallowOrCtx?.shallow);

      if (shallow) {
        return;
      }

      setVisible(true);
      setProgress(20);
      document.body.classList.add('is-navigating');
      timerRef.current = setInterval(() => {
        setProgress((current) => (current < 90 ? current + 4 : current));
      }, 180);
    };

    const handleClick = (event: MouseEvent) => {
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      ) {
        return;
      }

      const anchor = (event.target as HTMLElement).closest('a');
      if (
        !anchor ||
        anchor.target === '_blank' ||
        anchor.hasAttribute('download')
      ) {
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href || !isInternalLink(href)) {
        return;
      }

      const url = new URL(href, window.location.origin);
      const currentPath = router.asPath.split('?')[0];
      if (url.pathname === currentPath && !url.search) {
        return;
      }

      clearPendingLink();
      anchor.classList.add('nav-link-pending');
      pendingLinkRef.current = anchor;
    };

    document.addEventListener('click', handleClick, true);
    router.events.on('routeChangeStart', startNavigation);
    router.events.on('routeChangeComplete', completeNavigation);
    router.events.on('routeChangeError', completeNavigation);

    return () => {
      document.removeEventListener('click', handleClick, true);
      router.events.off('routeChangeStart', startNavigation);
      router.events.off('routeChangeComplete', completeNavigation);
      router.events.off('routeChangeError', completeNavigation);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      clearPendingLink();
    };
  }, [router]);

  if (!visible) {
    return null;
  }

  return (
    <div
      className={styles.bar}
      style={{ width: `${progress}%` }}
      aria-hidden="true"
    />
  );
};
